import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  InputBase,
  Avatar,
  CircularProgress,
  Badge,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { Thread } from '../../services/chatService';
import { Community } from '../../services/communityService';
import { CWY_COLORS } from '../../theme/colors';
import { avatarColor, getInitials } from '../community/chatHelpers';
const SUPPORT_DISPLAY_NAME = 'CWY Support';

interface MessagingThreadListProps {
  threads: Thread[];
  selectedThread: Thread | null;
  loadingThreads: boolean;
  onSelectThread: (thread: Thread) => void;
  onNewChat?: () => void;
  communities?: Community[];
  loadingCommunities?: boolean;
  selectedCommunityId?: string | null;
  unreadMap?: Record<string, number>;
  onSelectCommunity?: (community: Community) => void;
}

function getThreadPreview(thread: Thread): string {
  const last = thread.last_message;
  if (!last?.content) return 'No messages yet';
  if (last.message_type === 'call') return 'Voice call';
  if (last.message_type && last.message_type !== 'text') return 'Attachment';
  return last.content;
}

function getCommunityPreview(community: Community): string {
  if (community.last_message_preview?.trim()) return community.last_message_preview;
  const count = community.member_count;
  return count === 1 ? '1 member' : `${count} members`;
}

function formatListTime(timestamp?: string): string {
  if (!timestamp) return '';
  const date = /^\d+$/.test(timestamp) ? new Date(Number(timestamp) * 1000) : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

interface ListRowProps {
  title: string;
  preview: string;
  time?: string;
  unread: number;
  isSelected: boolean;
  onClick: () => void;
  avatar: React.ReactNode;
}

const ListRow: React.FC<ListRowProps> = ({
  title,
  preview,
  time,
  unread,
  isSelected,
  onClick,
  avatar,
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      width: '100%',
      px: 2,
      py: 1.5,
      border: 'none',
      borderBottom: `1px solid ${CWY_COLORS.border}`,
      bgcolor: isSelected ? 'var(--cwy-border, #eef3f8)' : 'var(--cwy-cardBg, #fff)',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'background-color 0.15s ease',
      '&:hover': {
        bgcolor: isSelected ? 'var(--cwy-border, #eef3f8)' : 'var(--cwy-borderSoft, #f3f2ef)',
      },
    }}
  >
    {avatar}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: unread > 0 ? 700 : 600,
            color: CWY_COLORS.textPrimary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
        {time && (
          <Typography variant="caption" sx={{ color: CWY_COLORS.textSecondary, flexShrink: 0 }}>
            {time}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.25 }}>
        <Typography
          variant="body2"
          sx={{
            color: CWY_COLORS.textSecondary,
            fontSize: '0.8125rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: unread > 0 ? 600 : 400,
          }}
        >
          {preview}
        </Typography>
        {unread > 0 && (
          <Badge
            badgeContent={unread}
            color="primary"
            sx={{
              '& .MuiBadge-badge': {
                position: 'static',
                transform: 'none',
                fontSize: 10,
              },
            }}
          />
        )}
      </Box>
    </Box>
  </Box>
);

function parseListTimestamp(timestamp?: string): number {
  if (!timestamp) return 0;
  const date = /^\d+$/.test(timestamp) ? new Date(Number(timestamp) * 1000) : new Date(timestamp);
  const ms = date.getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

type ListItem =
  | {
      kind: 'thread';
      id: string;
      title: string;
      preview: string;
      time?: string;
      sortTime: number;
      unread: number;
      isSelected: boolean;
      onClick: () => void;
      avatar: React.ReactNode;
    }
  | {
      kind: 'community';
      id: string;
      title: string;
      preview: string;
      time?: string;
      sortTime: number;
      unread: number;
      isSelected: boolean;
      onClick: () => void;
      avatar: React.ReactNode;
    };

function SectionHeader({ label }: { label: string }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        px: 2,
        py: 1,
        fontWeight: 700,
        fontSize: '0.6875rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: CWY_COLORS.textSecondary,
        bgcolor: 'var(--cwy-bg, #f8f9fa)',
        borderBottom: `1px solid ${CWY_COLORS.border}`,
      }}
    >
      {label}
    </Typography>
  );
}

function buildThreadItems(
  threads: Thread[],
  search: string,
  selectedCommunityId: string | null | undefined,
  selectedThreadId: string | undefined,
  onSelectThread: (thread: Thread) => void
): ListItem[] {
  const q = search.trim().toLowerCase();
  const items: ListItem[] = [];

  for (const thread of threads) {
    if (
      q &&
      !SUPPORT_DISPLAY_NAME.toLowerCase().includes(q) &&
      !(thread.title?.toLowerCase().includes(q) ?? false)
    ) {
      continue;
    }
    items.push({
      kind: 'thread',
      id: thread._id,
      title: thread.title?.trim() || SUPPORT_DISPLAY_NAME,
      preview: getThreadPreview(thread),
      time: formatListTime(thread.last_message?.timestamp),
      sortTime: parseListTimestamp(thread.last_message?.timestamp),
      unread: thread.unread_count ?? 0,
      isSelected: !selectedCommunityId && selectedThreadId === thread._id,
      onClick: () => onSelectThread(thread),
      avatar: (
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: CWY_COLORS.red,
            fontSize: '1rem',
            flexShrink: 0,
          }}
        >
          C
        </Avatar>
      ),
    });
  }

  return items.sort((a, b) => b.sortTime - a.sortTime);
}

function buildCommunityItems(
  communities: Community[],
  search: string,
  selectedCommunityId: string | null | undefined,
  unreadMap: Record<string, number>,
  onSelectCommunity?: (community: Community) => void
): ListItem[] {
  const q = search.trim().toLowerCase();
  const items: ListItem[] = [];

  for (const community of communities) {
    if (
      q &&
      !community.name.toLowerCase().includes(q) &&
      !(community.last_message_preview?.toLowerCase().includes(q) ?? false)
    ) {
      continue;
    }
    items.push({
      kind: 'community',
      id: community.community_id,
      title: community.name,
      preview: getCommunityPreview(community),
      time: formatListTime(community.last_message_at),
      sortTime: parseListTimestamp(community.last_message_at),
      unread: unreadMap[community.community_id] ?? community.unread_count ?? 0,
      isSelected: selectedCommunityId === community.community_id,
      onClick: () => onSelectCommunity?.(community),
      avatar: (
        <Avatar
          src={community.avatar_url}
          sx={{
            width: 48,
            height: 48,
            bgcolor: avatarColor(community.community_id),
            fontSize: '0.875rem',
            flexShrink: 0,
          }}
        >
          {community.avatar_url ? null : getInitials(community.name)}
        </Avatar>
      ),
    });
  }

  return items.sort((a, b) => b.sortTime - a.sortTime);
}

export const MessagingThreadList: React.FC<MessagingThreadListProps> = ({
  threads,
  selectedThread,
  loadingThreads,
  onSelectThread,
  communities = [],
  loadingCommunities = false,
  selectedCommunityId = null,
  unreadMap = {},
  onSelectCommunity,
}) => {
  const [search, setSearch] = useState('');

  const supportItems = useMemo(
    () =>
      buildThreadItems(
        threads,
        search,
        selectedCommunityId,
        selectedThread?._id,
        onSelectThread
      ),
    [threads, search, selectedCommunityId, selectedThread?._id, onSelectThread]
  );

  const communityItems = useMemo(
    () =>
      buildCommunityItems(communities, search, selectedCommunityId, unreadMap, onSelectCommunity),
    [communities, search, selectedCommunityId, unreadMap, onSelectCommunity]
  );

  const isLoading =
    loadingThreads && threads.length === 0 && loadingCommunities && communities.length === 0;
  const isEmpty = supportItems.length === 0 && communityItems.length === 0;

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 360 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: { md: `1px solid ${CWY_COLORS.border}` },
        bgcolor: 'var(--cwy-cardBg, #fff)',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${CWY_COLORS.border}`,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          Messaging
        </Typography>
      </Box>

      <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'var(--cwy-bg, #eef3f8)',
            borderRadius: '4px',
            px: 1.5,
            py: 0.75,
          }}
        >
          <SearchIcon sx={{ color: CWY_COLORS.textSecondary, fontSize: 18, mr: 1 }} />
          <InputBase
            placeholder="Search messages"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, fontSize: '0.875rem' }}
          />
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : isEmpty ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {search ? 'No conversations match your search' : 'No conversations yet'}
            </Typography>
          </Box>
        ) : (
          <>
            {supportItems.length > 0 && (
              <>
                <SectionHeader label="Support" />
                {supportItems.map((item) => (
                  <ListRow
                    key={`${item.kind}-${item.id}`}
                    title={item.title}
                    preview={item.preview}
                    time={item.time}
                    unread={item.unread}
                    isSelected={item.isSelected}
                    onClick={item.onClick}
                    avatar={item.avatar}
                  />
                ))}
              </>
            )}
            {communityItems.length > 0 && (
              <>
                <SectionHeader label="Communities" />
                {communityItems.map((item) => (
                  <ListRow
                    key={`${item.kind}-${item.id}`}
                    title={item.title}
                    preview={item.preview}
                    time={item.time}
                    unread={item.unread}
                    isSelected={item.isSelected}
                    onClick={item.onClick}
                    avatar={item.avatar}
                  />
                ))}
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
